import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useWeb3 } from '../context/Web3Context';
import { toast } from 'sonner';
import { 
  ArrowLeft, Home, Shield, FileText, CheckCircle, AlertTriangle, 
  Calculator, DollarSign, Calendar, Lock,
  Building, RefreshCw, Plus
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Treasury-Indexed Mortgage Obligation Products for NEW financing
const TIMO_PRODUCTS = [
  {
    id: 'timo-15',
    name: 'TIMO-15 (15-Year)',
    description: '15-year Treasury-indexed mortgage obligation',
    term: 180, // months
    referenceRate: 'U.S. Treasury 10-Year CMR',
    baseAPR: 5.25,
    spread: 1.50,
    minAmount: 100000,
    maxAmount: 2000000,
    features: ['BETH Credit Enhancement', 'PAS Protection', 'Faster Payoff']
  },
  {
    id: 'timo-20',
    name: 'TIMO-20 (20-Year)',
    description: '20-year Treasury-indexed mortgage obligation',
    term: 240, // months
    referenceRate: 'U.S. Treasury 10-Year CMR',
    baseAPR: 5.50,
    spread: 1.75,
    minAmount: 100000,
    maxAmount: 3000000,
    features: ['BETH Credit Enhancement', 'PAS Protection', 'Balanced Term']
  },
  {
    id: 'timo-30',
    name: 'TIMO-30 (30-Year)',
    description: '30-year Treasury-indexed mortgage obligation',
    term: 360, // months
    referenceRate: 'U.S. Treasury 30-Year CMR',
    baseAPR: 5.75,
    spread: 2.00,
    minAmount: 150000,
    maxAmount: 5000000,
    features: ['BETH Credit Enhancement', 'PAS Protection', 'Lowest Payment']
  }
];

const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);
};

const NewMortgageFinancing = () => {
  const { user, token } = useAuth();
  const { account, bethBalance } = useWeb3();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [propertyValue, setPropertyValue] = useState('');
  const [loanAmount, setLoanAmount] = useState('');
  const [costSheet, setCostSheet] = useState(null);
  const [orders, setOrders] = useState([]);
  
  // Form for creating new mortgage financing
  const [formData, setFormData] = useState({
    borrower_name: '',
    borrower_wallet: '',
    property_address: '',
    property_type: 'single_family',
    property_city: '',
    property_state: '',
    property_zip: '',
    down_payment: '',
    beth_collateral: ''
  });

  useEffect(() => {
    if (!token) {
      navigate('/login');
      return;
    }
    fetchOrders();
  }, [token]);

  useEffect(() => {
    if (account) {
      setFormData(prev => ({ ...prev, borrower_wallet: account }));
    }
  }, [account]);

  useEffect(() => {
    if (user) {
      setFormData(prev => ({ ...prev, borrower_name: user.name || user.email }));
    }
  }, [user]);

  const fetchOrders = async () => {
    try {
      const res = await fetch(`${API}/mortgage-refinance/orders`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setOrders(data.orders?.filter(o => !o.is_refinance) || []);
      }
    } catch (error) {
      console.error('Error fetching orders:', error);
    }
  };

  const handleProductSelect = (product) => {
    setSelectedProduct(product);
    if (loanAmount) {
      calculateCostSheet(product, parseFloat(loanAmount));
    }
  };

  const handleLoanAmountChange = (value) => {
    setLoanAmount(value);
    if (selectedProduct && value) {
      calculateCostSheet(selectedProduct, parseFloat(value));
    }
  };

  const calculateCostSheet = (product, principal) => {
    if (!product || !principal || principal < product.minAmount) {
      setCostSheet(null);
      return;
    }
    
    const indexedAPR = product.baseAPR;
    const monthlyRate = indexedAPR / 100 / 12;
    const numPayments = product.term;
    
    const monthlyPayment = principal * (monthlyRate * Math.pow(1 + monthlyRate, numPayments)) / (Math.pow(1 + monthlyRate, numPayments) - 1);
    const totalPayments = monthlyPayment * numPayments;
    const totalIndexAdjustment = totalPayments - principal;
    
    // Platform fees
    const structuringFee = principal * 0.015; // 1.5% for mortgage
    const pasEnhancementFee = principal * 0.01;
    const originationFee = principal * 0.01;
    const titleFee = 1500;
    const totalFees = structuringFee + pasEnhancementFee + originationFee + titleFee;
    
    // BETH collateral requirement (10% of principal at $1.00/BETH)
    const bethRequired = Math.ceil(principal * 0.10);
    
    // LTV calculation
    const propValue = parseFloat(propertyValue) || principal;
    const ltv = ((principal / propValue) * 100).toFixed(1);
    
    setCostSheet({
      principal: principal.toFixed(2),
      propertyValue: propValue.toFixed(2),
      ltv: ltv,
      indexedAPR: indexedAPR.toFixed(2),
      term: product.term,
      termYears: product.term / 12,
      monthlyPayment: monthlyPayment.toFixed(2),
      totalPayments: totalPayments.toFixed(2),
      totalIndexAdjustment: totalIndexAdjustment.toFixed(2),
      structuringFee: structuringFee.toFixed(2),
      pasEnhancementFee: pasEnhancementFee.toFixed(2),
      originationFee: originationFee.toFixed(2),
      titleFee: titleFee.toFixed(2),
      totalFees: totalFees.toFixed(2),
      bethCollateral: bethRequired,
      collateralRatio: 10
    });
    
    setFormData(prev => ({ ...prev, beth_collateral: bethRequired.toString() }));
  };

  const handleCreateOrder = async () => {
    if (!selectedProduct || !costSheet) {
      toast.error('Please select a product and enter loan amount');
      return;
    }
    
    if (!formData.property_address) {
      toast.error('Please enter the property address');
      return;
    }

    setLoading(true);
    try {
      const fullAddress = `${formData.property_address}, ${formData.property_city}, ${formData.property_state} ${formData.property_zip}`;
      
      const res = await fetch(`${API}/mortgage-refinance/order`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          ...formData,
          property_address: fullAddress,
          product_id: selectedProduct.id,
          product_name: selectedProduct.name,
          principal_amount: parseFloat(costSheet.principal),
          property_value: parseFloat(costSheet.propertyValue),
          ltv: parseFloat(costSheet.ltv),
          indexed_apr: parseFloat(costSheet.indexedAPR),
          term_months: selectedProduct.term,
          monthly_payment: parseFloat(costSheet.monthlyPayment),
          treasury_index: selectedProduct.referenceRate,
          beth_collateral: parseFloat(costSheet.bethCollateral),
          is_refinance: false
        })
      });
      
      const data = await res.json();
      if (data.success) {
        toast.success('Mortgage Finance Order created successfully!');
        fetchOrders();
        setLoanAmount('');
        setPropertyValue('');
        setCostSheet(null);
        setFormData(prev => ({
          ...prev,
          property_address: '',
          property_city: '',
          property_state: '',
          property_zip: '',
          down_payment: ''
        }));
      } else {
        toast.error(data.error || 'Failed to create order');
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('Error creating mortgage order');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0a1628]" data-testid="new-mortgage-financing-page">
      {/* Header */}
      <header className="bg-[#0d1d35] border-b border-[#1a2d4a]">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Link to="/dashboard" className="text-gray-400 hover:text-white">
                <ArrowLeft className="w-5 h-5" />
              </Link>
              <div>
                <h1 className="text-xl font-bold text-white flex items-center gap-2">
                  <Home className="w-6 h-6 text-indigo-400" />
                  New Mortgage Financing
                </h1>
                <p className="text-sm text-gray-400">Treasury-Indexed Mortgage Obligation (TIMO) - New Property Purchase</p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <Link to="/mortgage-refinance">
                <Button variant="outline" className="border-purple-500/50 text-purple-400 hover:bg-purple-500/10">
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Refinance Existing Mortgage
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </header>

      {/* Disclaimer Banner */}
      <div className="bg-gradient-to-r from-yellow-900/30 to-yellow-800/30 border-b border-yellow-700/50">
        <div className="max-w-7xl mx-auto px-6 py-3">
          <div className="flex items-center gap-3 text-yellow-200 text-xs">
            <AlertTriangle className="w-4 h-4 flex-shrink-0" />
            <p>
              <strong>IMPORTANT:</strong> This is a PRIVATE CREDIT structure for NEW property purchases, NOT a consumer mortgage. 
              Outside CFPB/TILA/RESPA jurisdiction.
            </p>
          </div>
        </div>
      </div>

      <main className="max-w-7xl mx-auto px-6 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Left Column - Product Selection & Property Details */}
          <div className="space-y-6">
            {/* Product Selection */}
            <Card className="bg-[#0d1d35] border-[#1a2d4a]">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Calendar className="w-5 h-5 text-indigo-400" />
                  Select Term Length
                </CardTitle>
                <CardDescription>Choose your mortgage term</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-3 gap-3">
                  {TIMO_PRODUCTS.map((product) => (
                    <div
                      key={product.id}
                      onClick={() => handleProductSelect(product)}
                      className={`p-4 rounded-lg border cursor-pointer transition-all ${
                        selectedProduct?.id === product.id 
                          ? 'border-indigo-500 bg-indigo-500/10' 
                          : 'border-[#1a2d4a] hover:border-indigo-500/50'
                      }`}
                      data-testid={`term-${product.term/12}yr`}
                    >
                      <div className="text-2xl font-bold text-white">{product.term/12}yr</div>
                      <div className="text-sm text-indigo-400">{product.baseAPR}% APR</div>
                      <div className="text-xs text-gray-500 mt-1">{product.referenceRate.replace('U.S. Treasury ', '').replace(' CMR', '')}</div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Property Details Form */}
            <Card className="bg-[#0d1d35] border-[#1a2d4a]">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Home className="w-5 h-5 text-indigo-400" />
                  Property & Loan Details
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs text-gray-400 uppercase tracking-wider">Property Value (USD)</label>
                    <Input
                      type="number"
                      value={propertyValue}
                      onChange={(e) => {
                        setPropertyValue(e.target.value);
                        if (selectedProduct && loanAmount) {
                          setTimeout(() => calculateCostSheet(selectedProduct, parseFloat(loanAmount)), 0);
                        }
                      }}
                      placeholder="Property Value"
                      className="mt-2 bg-[#0a1628] border-[#1a2d4a] text-white"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-gray-400 uppercase tracking-wider">Loan Amount (USD)</label>
                    <Input
                      type="number"
                      value={loanAmount}
                      onChange={(e) => handleLoanAmountChange(e.target.value)}
                      placeholder="Loan Amount"
                      className="mt-2 bg-[#0a1628] border-[#1a2d4a] text-white"
                      data-testid="loan-amount-input"
                    />
                  </div>
                </div>
                
                <div>
                  <label className="text-xs text-gray-400 uppercase tracking-wider">Property Type</label>
                  <select
                    value={formData.property_type}
                    onChange={(e) => setFormData(prev => ({ ...prev, property_type: e.target.value }))}
                    className="mt-2 w-full bg-[#0a1628] border border-[#1a2d4a] text-white rounded-md p-2"
                  >
                    <option value="single_family">Single Family Home</option>
                    <option value="condo">Condominium</option>
                    <option value="townhouse">Townhouse</option>
                    <option value="multi_family">Multi-Family (2-4 units)</option>
                    <option value="commercial">Commercial Property</option>
                  </select>
                </div>
                
                <div className="border-t border-[#1a2d4a] pt-4 mt-4">
                  <h4 className="text-sm text-gray-400 mb-3">Property Address</h4>
                  <div className="space-y-3">
                    <Input
                      value={formData.property_address}
                      onChange={(e) => setFormData(prev => ({ ...prev, property_address: e.target.value }))}
                      placeholder="Street Address"
                      className="bg-[#0a1628] border-[#1a2d4a] text-white"
                      data-testid="property-address-input"
                    />
                    <div className="grid grid-cols-3 gap-3">
                      <Input
                        value={formData.property_city}
                        onChange={(e) => setFormData(prev => ({ ...prev, property_city: e.target.value }))}
                        placeholder="City"
                        className="bg-[#0a1628] border-[#1a2d4a] text-white"
                      />
                      <Input
                        value={formData.property_state}
                        onChange={(e) => setFormData(prev => ({ ...prev, property_state: e.target.value.toUpperCase() }))}
                        placeholder="State"
                        maxLength={2}
                        className="bg-[#0a1628] border-[#1a2d4a] text-white"
                      />
                      <Input
                        value={formData.property_zip}
                        onChange={(e) => setFormData(prev => ({ ...prev, property_zip: e.target.value }))}
                        placeholder="ZIP"
                        maxLength={10}
                        className="bg-[#0a1628] border-[#1a2d4a] text-white"
                      />
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Right Column - Cost Sheet */}
          <div className="space-y-6">
            <Card className="bg-[#0d1d35] border-[#1a2d4a]">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <FileText className="w-5 h-5 text-indigo-400" />
                  Mortgage Cost Sheet
                </CardTitle>
              </CardHeader>
              <CardContent>
                {costSheet ? (
                  <div className="space-y-4">
                    <div className="p-4 bg-gradient-to-r from-[#0a1628] to-[#1a2d4a] rounded-lg border border-indigo-500/30">
                      <div className="text-xs text-indigo-400 uppercase tracking-wider mb-2">Monthly Payment (P&I)</div>
                      <div className="text-3xl font-bold text-white">${parseFloat(costSheet.monthlyPayment).toLocaleString()}</div>
                      <div className="text-sm text-gray-400 mt-1">for {costSheet.termYears} years at {costSheet.indexedAPR}% APR</div>
                    </div>
                    
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between py-2 border-b border-[#1a2d4a]">
                        <span className="text-gray-400">Property Value</span>
                        <span className="text-white">${parseFloat(costSheet.propertyValue).toLocaleString()}</span>
                      </div>
                      <div className="flex justify-between py-2 border-b border-[#1a2d4a]">
                        <span className="text-gray-400">Loan Amount</span>
                        <span className="text-white font-bold">${parseFloat(costSheet.principal).toLocaleString()}</span>
                      </div>
                      <div className="flex justify-between py-2 border-b border-[#1a2d4a]">
                        <span className="text-gray-400">Loan-to-Value (LTV)</span>
                        <span className={`font-bold ${parseFloat(costSheet.ltv) > 80 ? 'text-yellow-400' : 'text-green-400'}`}>{costSheet.ltv}%</span>
                      </div>
                      <div className="flex justify-between py-2 border-b border-[#1a2d4a]">
                        <span className="text-gray-400">Total of Payments</span>
                        <span className="text-white">${parseFloat(costSheet.totalPayments).toLocaleString()}</span>
                      </div>
                    </div>
                    
                    <div className="p-3 bg-blue-900/20 border border-blue-700/50 rounded-lg">
                      <div className="text-xs text-blue-400 mb-2 font-medium">CLOSING COSTS</div>
                      <div className="space-y-1 text-sm">
                        <div className="flex justify-between">
                          <span className="text-gray-400">Structuring Fee (1.5%)</span>
                          <span className="text-white">${parseFloat(costSheet.structuringFee).toLocaleString()}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-400">Origination Fee (1%)</span>
                          <span className="text-white">${parseFloat(costSheet.originationFee).toLocaleString()}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-400">PAS Enhancement (1%)</span>
                          <span className="text-white">${parseFloat(costSheet.pasEnhancementFee).toLocaleString()}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-400">Title & Recording</span>
                          <span className="text-white">${costSheet.titleFee}</span>
                        </div>
                        <div className="flex justify-between pt-2 border-t border-blue-700/50">
                          <span className="text-gray-400">Total Closing Costs</span>
                          <span className="text-indigo-400 font-bold">${parseFloat(costSheet.totalFees).toLocaleString()}</span>
                        </div>
                      </div>
                    </div>
                    
                    <div className="p-3 bg-[#0a1628] rounded-lg">
                      <div className="flex items-center gap-2 text-xs text-indigo-400 mb-2">
                        <Lock className="w-4 h-4" />
                        BETH CREDIT ENHANCEMENT
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-400">BETH Required</span>
                        <span className="text-white">{parseFloat(costSheet.bethCollateral).toLocaleString()} BETH</span>
                      </div>
                      <div className="flex justify-between text-sm mt-1">
                        <span className="text-gray-400">Collateralization</span>
                        <span className="text-white">{costSheet.collateralRatio}%</span>
                      </div>
                    </div>
                    
                    <Button 
                      className="w-full bg-indigo-600 hover:bg-indigo-700"
                      onClick={handleCreateOrder}
                      disabled={loading || !formData.property_address}
                      data-testid="create-mortgage-order-btn"
                    >
                      {loading ? 'Creating...' : 'Create Mortgage Order'}
                      <Plus className="w-4 h-4 ml-2" />
                    </Button>
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    <Calculator className="w-12 h-12 mx-auto mb-3 opacity-30" />
                    <p>Select a term and enter loan amount to see cost breakdown</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* My Orders */}
            {orders.length > 0 && (
              <Card className="bg-[#0d1d35] border-[#1a2d4a]">
                <CardHeader>
                  <CardTitle className="text-white text-sm">My Mortgage Orders ({orders.length})</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {orders.slice(0, 3).map((order) => (
                      <div key={order.order_id} className="p-3 bg-[#0a1628] rounded-lg">
                        <div className="flex justify-between items-start">
                          <div>
                            <div className="text-white text-sm font-medium">{order.property_address?.substring(0, 30) || 'Property'}...</div>
                            <div className="text-xs text-gray-500">{order.order_id}</div>
                          </div>
                          <div className="text-right">
                            <div className="text-indigo-400 font-medium">${parseFloat(order.principal_amount || 0).toLocaleString()}</div>
                            <div className={`text-xs px-2 py-0.5 rounded ${order.status === 'active' ? 'bg-green-500/20 text-green-400' : 'bg-yellow-500/20 text-yellow-400'}`}>
                              {order.status?.toUpperCase()}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default NewMortgageFinancing;
